import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import "./HomeBody/HomeBody.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { useEpkWizard } from '../context/EpkWizardContext';
import { useTranslation } from 'react-i18next';

const aws = process.env.REACT_APP_AWS_URL;
const SLIDE_DURATION = 5000;

/* ── Shared gradient overlays ─────────────────────────────── */
function Overlays() {
  return (
    <>
      <div className="tw-absolute tw-inset-0 tw-pointer-events-none" style={{ background: 'rgba(20,0,40,0.25)' }} />
      <div className="tw-absolute tw-inset-0 tw-pointer-events-none" style={{ background: 'linear-gradient(to right, rgba(30,0,57,0.85) 0%, rgba(30,0,57,0.4) 30%, transparent 55%)' }} />
      <div className="tw-absolute tw-inset-0 tw-pointer-events-none" style={{ background: 'linear-gradient(to top, rgba(20,0,40,0.92) 0%, rgba(20,0,40,0.55) 25%, transparent 55%)' }} />
    </>
  );
}

/* ── Shared poster card ───────────────────────────────────── */
function PosterCard({ href, src, badge }) {
  return (
    <a href={href} className="tw-group tw-flex-shrink-0 tw-hidden md:tw-block tw-no-underline">
      <div
        className="tw-relative tw-overflow-hidden tw-rounded-xl tw-border tw-border-[#FF43A7]/40 tw-shadow-[0_8px_40px_rgba(255,67,167,0.3)] tw-transition-all tw-duration-300 group-hover:tw-shadow-[0_12px_56px_rgba(255,67,167,0.5)] group-hover:tw-border-[#FF43A7]/70"
        style={{ width: '156px', aspectRatio: '2/3' }}
      >
        <img src={src} alt="" className="tw-w-full tw-h-full tw-object-cover tw-transition-transform tw-duration-500 group-hover:tw-scale-105" />
        {badge && (
          <span className="tw-absolute tw-top-2 tw-left-2 tw-rounded-full tw-bg-[#FF43A7]/90 tw-px-2.5 tw-py-0.5 tw-text-[9px] tw-font-bold tw-text-white tw-uppercase tw-tracking-wide">
            {badge}
          </span>
        )}
      </div>
    </a>
  );
}

/* ── Shared CTA button ────────────────────────────────────── */
function CtaButton({ href, label }) {
  return (
    <a href={href} className="tw-no-underline tw-w-fit tw-mt-1">
      <button className="tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-bg-[#FF43A7] tw-px-6 tw-py-2.5 tw-text-sm tw-font-bold tw-text-white hover:tw-bg-[#ff5cac] tw-border-0 tw-cursor-pointer tw-transition-all tw-shadow-[0_0_20px_rgba(255,67,167,0.45)] hover:tw-shadow-[0_0_32px_rgba(255,67,167,0.65)]">
        {label} <FontAwesomeIcon icon={faArrowRight} className="tw-text-xs" />
      </button>
    </a>
  );
}

/* ── Featured badge ───────────────────────────────────────── */
function FeaturedBadge({ label }) {
  return (
    <span className="tw-inline-flex tw-w-fit tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-[#FF43A7] tw-px-3 tw-py-1 tw-text-[10px] tw-font-bold tw-text-white tw-uppercase tw-tracking-widest tw-shadow-[0_0_14px_rgba(255,67,167,0.55)]">
      <FontAwesomeIcon icon={faStar} className="tw-text-[9px]" />
      {label}
    </span>
  );
}

/* ── Meta pill ────────────────────────────────────────────── */
function MetaPill({ label }) {
  return (
    <span className="tw-rounded-full tw-bg-[#5A3F49]/60 tw-border tw-border-[#5A3F49]/50 tw-px-3 tw-py-0.5 tw-text-xs tw-text-[#E2BDC9]">
      {label}
    </span>
  );
}

/* ── EPK slide ────────────────────────────────────────────── */
function EpkSlide({ fepk, active }) {
  const { t } = useTranslation();
  const bannerUrl = fepk.banner_url && !fepk.banner_url.startsWith('https') && fepk.banner_url !== ''
    ? `${aws}/${fepk.banner_url}` : null;
  const posterUrl = fepk.image_details && !fepk.image_details.startsWith('https')
    ? `${aws}/${fepk.image_details}` : null;
  const href = fepk.title ? `/epk/${fepk._id}` : '/';

  return (
    <div className={`tw-absolute tw-inset-0 tw-transition-opacity tw-duration-700 ${active ? 'tw-opacity-100 tw-z-10' : 'tw-opacity-0 tw-z-0 tw-pointer-events-none'}`}>
      {bannerUrl && <img src={bannerUrl} alt="" className="tw-absolute tw-inset-0 tw-w-full tw-h-full tw-object-cover tw-select-none tw-pointer-events-none" />}
      <Overlays />
      <div className="tw-relative tw-h-full tw-flex tw-items-end tw-gap-6 md:tw-gap-8 tw-px-5 md:tw-px-10 tw-pb-10 md:tw-pb-12">
        {posterUrl && <PosterCard href={href} src={posterUrl} badge={fepk.production_type} />}
        <div className="tw-flex tw-flex-col tw-gap-3">
          <FeaturedBadge label={t("Editor's Pick")} />
          <a href={href} className="tw-no-underline">
            <h2 className="tw-text-3xl sm:tw-text-4xl md:tw-text-5xl tw-font-extrabold tw-text-white tw-m-0 tw-leading-tight hover:tw-text-[#FF43A7] tw-transition-colors tw-duration-200" style={{ textShadow: '0 2px 24px rgba(0,0,0,0.7)' }}>
              {fepk.title}
            </h2>
          </a>
          {(fepk.production_type || fepk.status) && (
            <div className="tw-flex tw-flex-wrap tw-gap-2">
              {fepk.production_type && <MetaPill label={fepk.production_type} />}
              {fepk.status && <MetaPill label={t(fepk.status)} />}
            </div>
          )}
          {fepk.logLine_short && (
            <p className="tw-text-sm md:tw-text-base tw-text-[#E2BDC9] tw-m-0 tw-leading-relaxed tw-max-w-xl" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {fepk.logLine_short}
            </p>
          )}
          <CtaButton href={href} label={t("View EPK")} />
        </div>
      </div>
    </div>
  );
}

/* ── Actor slide ──────────────────────────────────────────── */
function ActorSlide({ actor, active }) {
  const { t } = useTranslation();
  const bannerUrl = actor.thumbnail && !actor.thumbnail.startsWith('https') ? `${aws}/${actor.thumbnail}` : null;
  const posterUrl = actor.picture ? (actor.picture.startsWith('https') ? actor.picture : `${aws}/${actor.picture}`) : null;
  const bgUrl = bannerUrl || posterUrl;
  const href = `/user/${actor._id}`;
  const name = `${actor.firstName || ''} ${actor.lastName || ''}`.trim();

  return (
    <div className={`tw-absolute tw-inset-0 tw-transition-opacity tw-duration-700 ${active ? 'tw-opacity-100 tw-z-10' : 'tw-opacity-0 tw-z-0 tw-pointer-events-none'}`}>
      {bgUrl && (
        <img
          src={bgUrl}
          alt=""
          className="tw-absolute tw-inset-0 tw-w-full tw-h-full tw-object-cover tw-select-none tw-pointer-events-none"
          style={{ objectPosition: bannerUrl ? 'center' : 'center 20%' }}
        />
      )}
      <Overlays />
      <div className="tw-relative tw-h-full tw-flex tw-items-end tw-gap-6 md:tw-gap-8 tw-px-5 md:tw-px-10 tw-pb-10 md:tw-pb-12">
        {posterUrl && <PosterCard href={href} src={posterUrl} badge={actor.role ? t(actor.role) : actor.role} />}
        <div className="tw-flex tw-flex-col tw-gap-3">
          <FeaturedBadge label={t("In the Spotlight")} />
          <a href={href} className="tw-no-underline">
            <h2 className="tw-text-3xl sm:tw-text-4xl md:tw-text-5xl tw-font-extrabold tw-text-white tw-m-0 tw-leading-tight hover:tw-text-[#FF43A7] tw-transition-colors tw-duration-200" style={{ textShadow: '0 2px 24px rgba(0,0,0,0.7)' }}>
              {name}
            </h2>
          </a>
          {actor.role && <div className="tw-flex tw-gap-2"><MetaPill label={t(actor.role)} /></div>}
          <CtaButton href={href} label={t("View Profile")} />
        </div>
      </div>
    </div>
  );
}

/* ── Promo slide ──────────────────────────────────────────── */
function PromoSlide({ active }) {
  const { openWizard } = useEpkWizard();
  const { t } = useTranslation();
  return (
    <div className={`tw-absolute tw-inset-0 tw-transition-opacity tw-duration-700 ${active ? 'tw-opacity-100 tw-z-10' : 'tw-opacity-0 tw-z-0 tw-pointer-events-none'}`}>
      <div className="tw-absolute tw-inset-0" style={{ background: 'radial-gradient(ellipse at 75% 50%, rgba(255,67,167,0.12) 0%, transparent 60%), radial-gradient(ellipse at 20% 80%, rgba(113,44,176,0.18) 0%, transparent 50%)' }} />
      <div className="tw-absolute tw-inset-0 tw-opacity-[0.04]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.8) 1px, transparent 1px)', backgroundSize: '48px 48px' }} />
      <div className="tw-relative tw-h-full tw-flex tw-items-center tw-px-5 md:tw-px-16">
        <div className="tw-flex tw-flex-col tw-gap-4 tw-max-w-2xl">
          <FeaturedBadge label={t("KinoKlik")} />
          <h2 className="tw-text-3xl sm:tw-text-4xl md:tw-text-5xl tw-font-extrabold tw-text-white tw-m-0 tw-leading-tight" style={{ textShadow: '0 2px 24px rgba(0,0,0,0.4)' }}>
            {t("Your Film.")}<br />{t("Your Story.")}
          </h2>
          <p className="tw-text-base md:tw-text-lg tw-text-[#E2BDC9] tw-m-0 tw-leading-relaxed tw-max-w-lg">
            {t("Create a stunning EPK and connect with distributors, producers, and industry players worldwide.")}
          </p>
          <button
            onClick={openWizard}
            className="tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-bg-[#FF43A7] tw-px-6 tw-py-2.5 tw-text-sm tw-font-bold tw-text-white hover:tw-bg-[#ff5cac] tw-border-0 tw-cursor-pointer tw-transition-all tw-shadow-[0_0_20px_rgba(255,67,167,0.45)] hover:tw-shadow-[0_0_32px_rgba(255,67,167,0.65)] tw-mt-1"
          >
            {t("Create Your EPK")} <FontAwesomeIcon icon={faArrowRight} className="tw-text-xs" />
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Main carousel ────────────────────────────────────────── */
export default function HomeHead({ role, data }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timerKey = useRef(0);

  const isPerson = role === 'actor' || role === 'filmmaker';

  const slides = useMemo(() => {
    const items = (data || []).filter(item => isPerson ? item.picture : item.image_details).slice(0, 5);
    const typed = items.map(item => ({ type: isPerson ? 'actor' : 'epk', data: item }));
    return [...typed, { type: 'promo' }];
  }, [data, isPerson]);

  const goTo = useCallback((i) => {
    setActiveIndex(i);
    timerKey.current += 1;
  }, []);

  const advance = useCallback(() => {
    setActiveIndex(i => (i + 1) % slides.length);
  }, [slides.length]);

  useEffect(() => {
    if (paused || slides.length <= 1) return;
    const id = setInterval(advance, SLIDE_DURATION);
    return () => clearInterval(id);
  }, [paused, advance, slides.length, timerKey.current]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!slides.length) return null;

  return (
    <div
      className="kk-dark-section home-head-animate tw-relative tw-overflow-hidden tw-bg-[#1e0039] tw-min-h-[400px] md:tw-min-h-[480px]"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* Slides */}
      {slides.map((slide, i) => {
        if (slide.type === 'epk')   return <EpkSlide   key={i} fepk={slide.data}  active={i === activeIndex} />;
        if (slide.type === 'actor') return <ActorSlide key={i} actor={slide.data} active={i === activeIndex} />;
        return <PromoSlide key="promo" active={i === activeIndex} />;
      })}

      {/* Pink top accent line */}
      <div className="tw-absolute tw-top-0 tw-inset-x-0 tw-h-[2px] tw-z-20 tw-pointer-events-none" style={{ background: 'linear-gradient(to right, #FF43A7 0%, rgba(255,67,167,0.4) 60%, transparent 100%)' }} />

      {/* Dot navigation */}
      <div className="tw-absolute tw-bottom-4 tw-right-5 tw-z-20 tw-flex tw-items-center tw-gap-2">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`tw-rounded-full tw-border-none tw-cursor-pointer tw-p-0 tw-transition-all tw-duration-300 ${
              i === activeIndex
                ? 'tw-w-6 tw-h-2 tw-bg-[#FF43A7]'
                : 'tw-w-2 tw-h-2 tw-bg-[#E2BDC9] hover:tw-bg-white'
            }`}
          />
        ))}
      </div>

      {/* Progress bar */}
      <div className="tw-absolute tw-bottom-0 tw-inset-x-0 tw-h-[2px] tw-z-20 tw-bg-[#5A3F49]/30">
        {!paused && (
          <div
            key={`${activeIndex}-${timerKey.current}`}
            className="tw-h-full tw-bg-[#FF43A7]"
            style={{ animation: `progress-fill ${SLIDE_DURATION}ms linear forwards` }}
          />
        )}
      </div>
    </div>
  );
}
